/**
 * Utility functions for role-based permissions and UI state
 */

import type { UserRole } from "./userTypes";

/**
 * Checks if the user has read-only access
 * @param role - The user's role
 * @returns True if the user is read-only
 */
export const isReadOnlyUser = (role: UserRole): boolean => {
  return role === "TB_GENERAL_READONLY";
};

/**
 * Checks if the user is an ECP Admin
 * @param role - The user's role
 * @returns True if the user is an ECP Admin
 */
export const isECPAdmin = (role: UserRole): boolean => {
  return role === "TB_ECP_ADMIN";
};

/**
 * Checks if the user is a Platform Admin
 * @param role - The user's role
 * @returns True if the user is a Platform Admin
 */
export const isPlatformAdmin = (role: UserRole): boolean => {
  return role === "TB_PLATFORM_ADMIN";
};

/**
 * Checks if the user is a Client Admin
 * @param role - The user's role
 * @returns True if the user is a Client Admin
 */
export const isClientAdmin = (role: UserRole): boolean => {
  return role === "TB_CLIENT_ADMIN";
};

export const canManageSelectedTemplate = (
  role: UserRole,
  selectedTemplateId: string | undefined,
  accountId: string | undefined,
  clientId: string | undefined,
): boolean => {
  if (!selectedTemplateId || isReadOnlyUser(role)) return false;
  if (isPlatformAdmin(role)) return Boolean(accountId);
  if (isClientAdmin(role)) return Boolean(accountId && clientId);
  return true;
};

/**
 * Checks if the user can deactivate a template version
 * Only Platform Admin and ECP Admin can deactivate
 */
export const canDeactivateVersion = (role: UserRole): boolean => {
  return role === "TB_ECP_ADMIN" || role === "TB_PLATFORM_ADMIN";
};

/**
 * Gets the disabled state object for all template action buttons
 * based on user role (for TB_GENERAL_READONLY users)
 * @param role - The user's role
 * @returns Object with disabled states for each action button
 */
export const getReadOnlyButtonStates = (role: UserRole) => {
  const isReadOnly = isReadOnlyUser(role);
  return {
    isPublishDisabled: isReadOnly,
    isRenameDisabled: isReadOnly,
    isCloneDisabled: isReadOnly,
    isMoveDisabled: isReadOnly,
    isDeleteDisabled: isReadOnly,
    isSaveAsDraftDisabled: isReadOnly,
    isDropdownDisabled: false, // Version dropdown stays enabled
  };
};

/**
 * Gets a placeholder option for read-only users in dropdowns
 * @returns Dropdown option object with "N/A" value
 */
export const getReadOnlyDropdownOption = () => ({
  id: "0",
  label: "N/A",
  value: "",
  title: "N/A - Read Only Access",
});
